// Mock K-beauty product data for development

import type { LegacyProduct, BudgetRoutines, RoutineStep } from '@/types/product'

// Essential Tier Products
export const essentialProducts: LegacyProduct[] = [
  {
    id: 'e-cleanser-01',
    name: 'Low pH Good Morning Gel Cleanser',
    brand: 'COSRX',
    category: 'cleanser',
    price: 12,
    imageUrl: '/image/products/cosrx-cleanser.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B016NRXO06',
    keyIngredients: ['tea-tree', 'BHA'],
    concerns: ['acne', 'pores'],
    skinTypes: ['oily', 'combination', 'normal'],
    description: 'Gentle, low pH gel cleanser that effectively cleanses without stripping.',
    size: '150ml',
    budgetTier: 'essential',
  },
  {
    id: 'e-toner-01',
    name: 'Galactomyces 95 Tone Balancing Essence',
    brand: 'COSRX',
    category: 'toner',
    price: 19,
    imageUrl: '/image/products/cosrx-galactomyces.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B013PIHDS2',
    keyIngredients: ['galactomyces', 'niacinamide'],
    concerns: ['dullness', 'texture'],
    skinTypes: ['dry', 'normal', 'combination', 'oily'],
    description: 'Brightening essence toner with 95% galactomyces ferment filtrate.',
    size: '100ml',
    budgetTier: 'essential',
  },
  {
    id: 'e-serum-01',
    name: 'Snail 96 Mucin Power Essence',
    brand: 'COSRX',
    category: 'serum',
    price: 21,
    imageUrl: '/image/products/cosrx-snail.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B00PBX3L7K',
    keyIngredients: ['snail-mucin', 'hyaluronic-acid'],
    concerns: ['dryness', 'texture', 'fine-lines'],
    skinTypes: ['dry', 'normal', 'combination', 'oily'],
    description: 'Lightweight essence with 96% snail secretion filtrate for repair and hydration.',
    size: '100ml',
    budgetTier: 'essential',
  },
  {
    id: 'e-moisturizer-01',
    name: 'Oil-Free Ultra-Moisturizing Lotion',
    brand: 'COSRX',
    category: 'moisturizer',
    price: 16,
    imageUrl: '/image/products/cosrx-lotion.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B01N3L9R8F',
    keyIngredients: ['birch-sap', 'hyaluronic-acid'],
    concerns: ['dryness', 'oiliness'],
    skinTypes: ['oily', 'combination'],
    description: 'Lightweight, oil-free moisturizer with 70% birch sap.',
    size: '100ml',
    budgetTier: 'essential',
  },
  {
    id: 'e-sunscreen-01',
    name: 'Aloe Soothing Sun Cream SPF50+',
    brand: 'COSRX',
    category: 'sunscreen',
    price: 15,
    imageUrl: '/image/products/cosrx-sunscreen.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B07B4PR7LH',
    keyIngredients: ['aloe-vera', 'centella'],
    concerns: ['sun-protection'],
    skinTypes: ['dry', 'normal', 'combination', 'oily'],
    description: 'Lightweight, non-greasy sunscreen with soothing aloe vera.',
    size: '50ml',
    budgetTier: 'essential',
  },
]

// Standard Tier Products
export const standardProducts: LegacyProduct[] = [
  {
    id: 's-cleanser-01',
    name: 'Green Tea Cleansing Foam',
    brand: 'Innisfree',
    category: 'cleanser',
    price: 18,
    imageUrl: '/image/products/innisfree-cleanser.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B00HUQY1L4',
    keyIngredients: ['green-tea', 'amino-acids'],
    concerns: ['oiliness', 'acne'],
    skinTypes: ['oily', 'combination'],
    description: 'Creamy foam cleanser with Jeju green tea for fresh, clean skin.',
    size: '150ml',
    budgetTier: 'standard',
  },
  {
    id: 's-toner-01',
    name: 'Klairs Supple Preparation Facial Toner',
    brand: 'Dear, Klairs',
    category: 'toner',
    price: 22,
    imageUrl: '/image/products/klairs-toner.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B00PGOFYG0',
    keyIngredients: ['hyaluronic-acid', 'centella'],
    concerns: ['dryness', 'sensitivity'],
    skinTypes: ['dry', 'normal', 'combination'],
    description: 'Deep hydrating toner that balances pH and preps skin.',
    size: '180ml',
    budgetTier: 'standard',
  },
  {
    id: 's-serum-01',
    name: 'Vitamin C 21.5 Advanced Serum',
    brand: 'By Wishtrend',
    category: 'serum',
    price: 28,
    imageUrl: '/image/products/wishtrend-vitc.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B00GY9XGKO',
    keyIngredients: ['vitamin-c', 'hippophae-rhamnoides'],
    concerns: ['dullness', 'pigmentation', 'fine-lines'],
    skinTypes: ['dry', 'normal', 'combination', 'oily'],
    description: 'High-concentration vitamin C serum for brightening and anti-aging.',
    size: '30ml',
    budgetTier: 'standard',
  },
  {
    id: 's-moisturizer-01',
    name: 'The True Cream Aqua Bomb',
    brand: 'belif',
    category: 'moisturizer',
    price: 38,
    imageUrl: '/image/products/belif-aquabomb.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B00BXLNLLG',
    keyIngredients: ['lady-mantle', 'squalane'],
    concerns: ['dryness', 'dullness'],
    skinTypes: ['oily', 'combination', 'normal'],
    description: 'Gel-cream moisturizer that provides intense hydration without heaviness.',
    size: '50ml',
    budgetTier: 'standard',
  },
  {
    id: 's-sunscreen-01',
    name: 'UV Defense Me. Mild Sun Gel SPF50+',
    brand: 'Missha',
    category: 'sunscreen',
    price: 18,
    imageUrl: '/image/products/missha-sunscreen.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B07HPTXRXW',
    keyIngredients: ['centella', 'squalane'],
    concerns: ['sun-protection', 'sensitivity'],
    skinTypes: ['dry', 'normal', 'combination', 'oily'],
    description: 'Lightweight gel sunscreen suitable for sensitive skin.',
    size: '70ml',
    budgetTier: 'standard',
  },
  {
    id: 's-treatment-01',
    name: 'Pure Retinol 0.5%',
    brand: 'By Wishtrend',
    category: 'treatment',
    price: 35,
    imageUrl: '/image/products/wishtrend-retinol.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B08DKZMK26',
    keyIngredients: ['retinol', 'bakuchiol'],
    concerns: ['fine-lines', 'wrinkles', 'texture'],
    skinTypes: ['normal', 'combination', 'oily'],
    description: 'Gentle retinol treatment for anti-aging and skin renewal.',
    size: '30ml',
    budgetTier: 'standard',
  },
]

// Premium Tier Products
export const premiumProducts: LegacyProduct[] = [
  {
    id: 'p-cleanser-01',
    name: 'Sulwhasoo Gentle Cleansing Foam',
    brand: 'Sulwhasoo',
    category: 'cleanser',
    price: 35,
    imageUrl: '/image/products/sulwhasoo-cleanser.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B00HSXLV2W',
    keyIngredients: ['ginseng', 'peach-kernel'],
    concerns: ['dullness', 'aging'],
    skinTypes: ['dry', 'normal', 'combination', 'oily'],
    description: 'Luxurious foam cleanser with herbal ingredients for a radiant finish.',
    size: '200ml',
    budgetTier: 'premium',
  },
  {
    id: 'p-toner-01',
    name: 'Essential Balancing Water EX',
    brand: 'Sulwhasoo',
    category: 'toner',
    price: 58,
    imageUrl: '/image/products/sulwhasoo-toner.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B00HSXLV2W',
    keyIngredients: ['ginseng', 'plum-blossom'],
    concerns: ['dullness', 'dryness', 'aging'],
    skinTypes: ['dry', 'normal', 'combination'],
    description: 'Luxurious hydrating toner with Korean herbal complex.',
    size: '150ml',
    budgetTier: 'premium',
  },
  {
    id: 'p-serum-01',
    name: 'First Care Activating Serum',
    brand: 'Sulwhasoo',
    category: 'serum',
    price: 89,
    imageUrl: '/image/products/sulwhasoo-serum.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B08TW1Q8VK',
    keyIngredients: ['ginseng', 'mugwort'],
    concerns: ['aging', 'dullness', 'elasticity'],
    skinTypes: ['dry', 'normal', 'combination', 'oily'],
    description: 'Anti-aging serum that enhances skin radiance and firmness.',
    size: '60ml',
    budgetTier: 'premium',
  },
  {
    id: 'p-moisturizer-01',
    name: 'Concentrated Ginseng Renewing Cream',
    brand: 'Sulwhasoo',
    category: 'moisturizer',
    price: 180,
    imageUrl: '/image/products/sulwhasoo-cream.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B00H3AE6EY',
    keyIngredients: ['ginseng', 'red-ginseng-oil'],
    concerns: ['aging', 'wrinkles', 'elasticity'],
    skinTypes: ['dry', 'normal'],
    description: 'Premium anti-aging cream with concentrated ginseng for firmness.',
    size: '60ml',
    budgetTier: 'premium',
  },
  {
    id: 'p-sunscreen-01',
    name: 'UV Wise Brightening Multi Protector SPF50+',
    brand: 'Sulwhasoo',
    category: 'sunscreen',
    price: 55,
    imageUrl: '/image/products/sulwhasoo-sunscreen.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B0B3JHFY4G',
    keyIngredients: ['white-ginseng', 'niacinamide'],
    concerns: ['sun-protection', 'brightening'],
    skinTypes: ['dry', 'normal', 'combination', 'oily'],
    description: 'Luxurious brightening sunscreen with anti-aging benefits.',
    size: '50ml',
    budgetTier: 'premium',
  },
  {
    id: 'p-eyecream-01',
    name: 'Concentrated Ginseng Renewing Eye Cream',
    brand: 'Sulwhasoo',
    category: 'eye-cream',
    price: 120,
    imageUrl: '/image/products/sulwhasoo-eye.jpg',
    affiliateLink: 'https://www.amazon.com/dp/B00H3AE6F8',
    keyIngredients: ['ginseng', 'peptides'],
    concerns: ['fine-lines', 'dark-circles', 'puffiness'],
    skinTypes: ['dry', 'normal', 'combination', 'oily'],
    description: 'Premium eye cream targeting wrinkles and dark circles.',
    size: '25ml',
    budgetTier: 'premium',
  },
]

// All products combined
export const allProducts: LegacyProduct[] = [
  ...essentialProducts,
  ...standardProducts,
  ...premiumProducts,
]

// Helper function to create routine step
function createStep(step: number, label: string, product: LegacyProduct, instructions: string): RoutineStep {
  return {
    step,
    category: product.category,
    label,
    product,
    instructions,
  }
}

// Mock routines for each budget tier
export const mockRoutines: BudgetRoutines = {
  essential: {
    am: [
      createStep(1, 'Cleanser', essentialProducts[0], 'Massage gently for 30-60 seconds, then rinse with lukewarm water.'),
      createStep(2, 'Toner', essentialProducts[1], 'Apply to cotton pad or pat directly onto skin.'),
      createStep(3, 'Serum', essentialProducts[2], 'Apply 2-3 drops and gently pat into skin.'),
      createStep(4, 'Moisturizer', essentialProducts[3], 'Apply a pea-sized amount and massage until absorbed.'),
      createStep(5, 'Sunscreen', essentialProducts[4], 'Apply generously as the last step. Reapply every 2 hours if outdoors.'),
    ],
    pm: [
      createStep(1, 'Cleanser', essentialProducts[0], 'Double cleanse if wearing makeup. Massage for 60 seconds.'),
      createStep(2, 'Toner', essentialProducts[1], 'Apply to cotton pad or pat directly onto skin.'),
      createStep(3, 'Serum', essentialProducts[2], 'Apply 2-3 drops and gently pat into skin.'),
      createStep(4, 'Moisturizer', essentialProducts[3], 'Apply a slightly larger amount than morning routine.'),
    ],
  },
  standard: {
    am: [
      createStep(1, 'Cleanser', standardProducts[0], 'Massage gently for 30-60 seconds, then rinse with lukewarm water.'),
      createStep(2, 'Toner', standardProducts[1], 'Apply to cotton pad or pat directly onto skin.'),
      createStep(3, 'Serum', standardProducts[2], 'Apply 2-3 drops and gently pat into skin. Wait 1 minute before next step.'),
      createStep(4, 'Moisturizer', standardProducts[3], 'Apply a pea-sized amount and massage until absorbed.'),
      createStep(5, 'Sunscreen', standardProducts[4], 'Apply generously as the last step. Reapply every 2 hours if outdoors.'),
    ],
    pm: [
      createStep(1, 'Cleanser', standardProducts[0], 'Double cleanse if wearing makeup. Massage for 60 seconds.'),
      createStep(2, 'Toner', standardProducts[1], 'Apply to cotton pad or pat directly onto skin.'),
      createStep(3, 'Serum', standardProducts[2], 'Apply 2-3 drops and gently pat into skin.'),
      createStep(4, 'Treatment', standardProducts[5], 'Apply every other night. Start with 2x/week and increase gradually.'),
      createStep(5, 'Moisturizer', standardProducts[3], 'Apply a slightly larger amount than morning routine.'),
    ],
  },
  premium: {
    am: [
      createStep(1, 'Cleanser', premiumProducts[0], 'Massage gently for 30-60 seconds, then rinse with lukewarm water.'),
      createStep(2, 'Toner', premiumProducts[1], 'Apply to cotton pad or pat directly onto skin.'),
      createStep(3, 'Serum', premiumProducts[2], 'Apply 2-3 drops and gently pat into skin.'),
      createStep(4, 'Eye Cream', premiumProducts[5], 'Gently pat around eye area using ring finger.'),
      createStep(5, 'Moisturizer', premiumProducts[3], 'Apply a pea-sized amount and massage until absorbed.'),
      createStep(6, 'Sunscreen', premiumProducts[4], 'Apply generously as the last step. Reapply every 2 hours if outdoors.'),
    ],
    pm: [
      createStep(1, 'Cleanser', premiumProducts[0], 'Double cleanse if wearing makeup. Massage for 60 seconds.'),
      createStep(2, 'Toner', premiumProducts[1], 'Apply to cotton pad or pat directly onto skin.'),
      createStep(3, 'Serum', premiumProducts[2], 'Apply 2-3 drops and gently pat into skin.'),
      createStep(4, 'Eye Cream', premiumProducts[5], 'Gently pat around eye area using ring finger.'),
      createStep(5, 'Moisturizer', premiumProducts[3], 'Apply a generous amount and massage until absorbed.'),
    ],
  },
}

// Calculate total routine cost
export function calculateRoutineCost(routine: RoutineStep[]): number {
  return routine.reduce((total, step) => total + step.product.price, 0)
}
